import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Dialog root component that controls the dialog state.
 * Use with DialogTrigger, DialogContent, and other dialog sub-components.
 */
const Dialog = DialogPrimitive.Root

/**
 * DialogTrigger component that opens the dialog when clicked.
 * Wraps the element that should trigger the dialog.
 */
const DialogTrigger = DialogPrimitive.Trigger

/**
 * DialogPortal component that renders the dialog in a portal.
 * Used internally by DialogContent.
 */
const DialogPortal = DialogPrimitive.Portal

/**
 * DialogClose component that closes the dialog when clicked.
 * Typically used as a close button inside the dialog.
 */
const DialogClose = DialogPrimitive.Close

/**
 * DialogOverlay component displays the backdrop behind the dialog.
 * Built on Radix UI Dialog primitive with blur effect.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>} props - Overlay props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Dialog overlay backdrop
 */
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[101] backdrop-blur-sm bg-[rgba(134,175,199,0.7)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * DialogContent component displays the main dialog content.
 * Includes overlay and handles animations. Built on Radix UI Dialog primitive.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>} props - Dialog content props
 * @param {React.ReactNode} props.children - Content to display inside the dialog
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Dialog content container with overlay
 * 
 * @example
 * ```tsx
 * <Dialog>
 *   <DialogTrigger>Open Dialog</DialogTrigger>
 *   <DialogContent>
 *     <DialogHeader>
 *       <DialogTitle>Title</DialogTitle>
 *       <DialogDescription>Description</DialogDescription>
 *     </DialogHeader>
 *     <p>Dialog content</p>
 *     <DialogFooter>
 *       <DialogClose>Close</DialogClose>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 * ```
 */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-[150] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

/**
 * DialogHeader component provides a container for dialog title and description.
 * 
 * @component
 * @param {React.HTMLAttributes<HTMLDivElement>} props - Standard div element props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Dialog header container
 */
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

/**
 * DialogTitle component displays the dialog title.
 * Built on Radix UI Dialog primitive for accessibility.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>} props - Title props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Dialog title element
 */
const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

/**
 * DialogDescription component displays descriptive text below the dialog title.
 * Built on Radix UI Dialog primitive for accessibility.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>} props - Description props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Dialog description element
 */
const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
